import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "legacy-application-refactoring",
  kind: "horizontal",
  title: "Legacy System Modernization",
  shortTitle: "Legacy Systems",
  status: "draft",
  tier: "we-do",
  seoTitle: "Legacy System Modernization | General Applications",
  seoDescription:
    "Incremental modernization of legacy web applications to type-safe frontends, current build tooling, and maintainable component systems.",
  summary:
    "Incremental modernization of the applications, data boundaries, interfaces, and operating practices that still need to work while their foundations are repaired.",
  image: "/images/blog/reconciling-fragmented-data-hero.webp",
  relatedCapabilitySlugs: ["software-architecture-engineering"],
  relatedIndustrySlugs: [
    "enterprise",
    "healthcare",
    "energy",
    "media-entertainment",
  ],
  relatedPostSlugs: ["legacy-system-modernization"],
  engineering: [
    "Incremental frontend, application, and build-system modernization",
    "Type-safe boundary, component, API, and data-contract recovery",
    "Legacy workflow, exception, state, and ownership analysis",
    "Migration sequencing that preserves operational continuity",
    "Test, observability, and rollback foundations for controlled change",
    "Replacement of brittle manual steps with explicit, maintainable system behavior",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "The System Still Has to Work While It Changes",
    },
    {
      type: "paragraph",
      text: "Legacy modernization is rarely a clean replacement. The old system holds real records, hidden operating knowledge, integrations, exceptions, and workarounds that people rely on even when they dislike it. The task is to recover enough of that reality to improve one boundary at a time without breaking the work the system still carries.",
    },
    {
      type: "heading",
      level: 2,
      text: "Start With the Boundaries That Create Risk",
    },
    {
      type: "paragraph",
      text: "An obsolete framework is not always the first problem to solve. Weak contracts, unclear ownership, undocumented state, fragile deployments, and missing test coverage make every later improvement expensive. Modernization begins by making those boundaries visible, then choosing a sequence that earns safer change rather than creating a second unmaintainable system beside the first.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map the system's users, workflows, data, integrations, deployment path, failure modes, and non-negotiable operating constraints before selecting a modernization sequence.",
      produces: [
        "Current-state architecture, dependency, and ownership map",
        "Workflow, data, interface, and integration risk inventory",
        "Operational-continuity, rollback, and release constraint analysis",
        "Modernization options and sequencing criteria",
      ],
      fit: {
        good: [
          "Teams with a valuable application or platform that needs safer change without a disruptive all-at-once replacement",
          "Organizations whose aging system mixes technical debt with undocumented workflow, integration, and ownership decisions",
        ],
        poor: [
          "A request to replace a system before establishing what it does today, who depends on it, and how a transition would preserve necessary records and work",
          "A cosmetic redesign that leaves unstable data, deployment, interface, and operational boundaries untouched",
        ],
      },
    },
    "initial-design": {
      focus:
        "Choose the first boundaries to stabilize and define a migration path that keeps the live system understandable and reversible.",
      produces: [
        "Target boundary and responsibility architecture",
        "Incremental migration, coexistence, and cutover sequence",
        "Data, interface, test, observability, and rollback strategy",
        "Delivery-risk and decision record",
      ],
    },
    "detailed-design": {
      focus:
        "Specify each change around the contracts, compatibility behavior, data movement, and verification that let old and new parts coexist safely.",
      produces: [
        "Interface, data-contract, and compatibility specifications",
        "Migration, synchronization, and rollback procedures",
        "Test, release, monitoring, and exception-handling definitions",
        "Component, workflow, and operational ownership model",
      ],
    },
    implementation: {
      focus:
        "Deliver the modernization in controlled increments, using production evidence to decide what should change next and what must remain stable.",
      produces: [
        "Stabilized application, interface, data, or deployment boundaries",
        "Verified migration and rollback paths",
        "Improved test, observability, and operational controls",
        "A maintainable sequence for the remaining modernization work",
      ],
    },
  },
};
